import React from 'react';
import { AlertTriangle, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useT } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';
import type { PortfolioZoneDto, ZoneHealth } from '@/api/portfolio';

// Hoisted from PortfolioPage (FE-39) so the «Все филиалы» dashboard/report
// boards share one card language with the superuser portfolio page.

export const HEALTH_RANK: Record<ZoneHealth, number> = { red: 0, yellow: 1, green: 2, unknown: 3 };

/**
 * Severity within a health bucket so the worst branch sorts first — the
 * multi-branch owner's "which one needs me most?" can't be a coin-flip between
 * two reds. Weighted by how costly each signal is (out-of-stock & long queues
 * lose money; overdue tasks & dirty tables are operational).
 */
export function severityOf(z: PortfolioZoneDto): number {
  let s = 0;
  if (z.layout_health === 'red') s += 40; else if (z.layout_health === 'yellow') s += 12;
  if (z.queue_health === 'red') s += 35; else if (z.queue_health === 'yellow') s += 10;
  s += (z.tasks.overdue ?? 0) * 6 + (z.tasks.pending ?? 0) * 1;
  s += (z.dirty_table_count ?? 0) * 4;
  return s;
}

/** Worst-first: health bucket, then severity within the bucket. */
export function rankZones(zones: PortfolioZoneDto[]): PortfolioZoneDto[] {
  return [...zones].sort(
    (a, b) =>
      HEALTH_RANK[a.overall_health] - HEALTH_RANK[b.overall_health] ||
      severityOf(b) - severityOf(a),
  );
}

const HEALTH_DOT: Record<ZoneHealth, string> = {
  red: 'animate-pulse bg-red-500',
  yellow: 'bg-amber-500',
  green: 'bg-emerald-500',
  unknown: 'bg-muted-foreground/40',
};

const HEALTH_RING: Record<ZoneHealth, string> = {
  red: 'ring-red-500/30',
  yellow: 'ring-amber-500/30',
  green: 'ring-emerald-500/20',
  unknown: '',
};

export function HealthDot({ health }: { health: ZoneHealth | null }) {
  const h: ZoneHealth = health ?? 'unknown';
  return (
    <span className={cn('inline-block h-2.5 w-2.5 rounded-full', HEALTH_DOT[h])} />
  );
}

export function ZoneHealthCard({ zone, onOpen }: { zone: PortfolioZoneDto; onOpen: (zoneId: string) => void }) {
  const { t } = useT();
  const health = zone.overall_health;

  const hasProblems =
    zone.tasks.overdue > 0 ||
    zone.tasks.pending > 0 ||
    (zone.dirty_table_count !== null && zone.dirty_table_count > 0) ||
    zone.layout_health === 'red' ||
    zone.layout_health === 'yellow' ||
    zone.queue_health === 'red' ||
    zone.queue_health === 'yellow';

  const id = String(zone.zone_id);
  return (
    <Card
      role="button"
      tabIndex={0}
      // Selecting text on the card must not navigate — only a real click does.
      onClick={() => { if (!document.getSelection()?.toString()) onOpen(id); }}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onOpen(id)}
      className={cn(
        'cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        health !== 'unknown' && `ring-1 ${HEALTH_RING[health]}`,
      )}
    >
      <CardContent className="space-y-3 p-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-2">
          <h3 className="truncate font-medium">{zone.zone_name}</h3>
          <HealthDot health={health} />
        </div>

        {/* Signal rows */}
        {!hasProblems ? (
          <div className="flex items-center gap-1.5 text-sm text-emerald-600">
            <span className="inline-block size-2 shrink-0 rounded-full bg-emerald-500" />
            {t('portfolio.allGreen')}
          </div>
        ) : (
          <ul className="space-y-1 text-sm">
            {zone.tasks.overdue > 0 && (
              <li className="flex items-center gap-1.5 text-destructive">
                <AlertTriangle className="h-3 w-3 shrink-0" />
                {t('portfolio.overdue', { n: zone.tasks.overdue })}
              </li>
            )}
            {zone.tasks.pending > 0 && (
              <li className="flex items-center gap-1.5 text-amber-600">
                <Clock className="h-3 w-3 shrink-0" />
                {t('portfolio.pending', { n: zone.tasks.pending })}
              </li>
            )}
            {zone.dirty_table_count !== null && zone.dirty_table_count > 0 && (
              <li className="flex items-center gap-1.5 text-amber-600">
                <span className="h-3 w-3 shrink-0" />
                {t('portfolio.dirtyTables', { n: zone.dirty_table_count })}
              </li>
            )}
            {(zone.layout_health === 'red' || zone.layout_health === 'yellow') && (
              <li className="flex items-center gap-1.5">
                <HealthDot health={zone.layout_health} />
                <span className="text-muted-foreground">{t('dashboard.cards.layout')}</span>
              </li>
            )}
            {(zone.queue_health === 'red' || zone.queue_health === 'yellow') && (
              <li className="flex items-center gap-1.5">
                <HealthDot health={zone.queue_health} />
                <span className="text-muted-foreground">{t('dashboard.cards.queue')}</span>
              </li>
            )}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
